import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { useIsCommunicationAdmin } from '../hooks/useIsCommunicationAdmin';
import { useIsCommunicationSupervisor } from '../hooks/useIsCommunicationSupervisor';
import { TimeTrackingAdmin } from '../components/TimeTrackingAdmin';
import { ZoomSweepPanel } from '../components/ZoomSweepPanel';
import { TranscriptRetrievalPanel } from '../components/TranscriptRetrievalPanel';
import { TranscriptViewer } from '../components/TranscriptViewer';
import { AllCallsFilterBar } from '../components/AllCallsFilterBar';
import { InsightsPanel } from '../components/insights/InsightsPanel';
import { PageLayout } from '../../../components/common/PageLayout';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { formatDateTimeLocal, formatDateTimeET, formatPhoneNumber } from '../format';
import { ui } from '../ui';
import {
  Agent, Category, Call, CallsFilter, CurrentAgent, ZoomUser, JcsaEmployee, MissedCallMetrics,
  CommunicationSettings, BusinessHoursDay, ZoomQueuePreview, RosterMember,
  getMe, getAgents, deactivateAgent, reactivateAgent, setAgentZoom, setAgentJcsaEmployee, setAgentRoster,
  getJcsaEmployees, getZoomUsers, getTimeTrackingRoster,
  getCategories, deactivateCategory,
  getAllCalls, getUnmappedCalls, assignCall, simulateCall, getMissedMetrics,
  getSettings, updateSettings, previewZoomQueue,
} from '../api';
import {
  POLL_MS, MetricsPreset, METRICS_PRESETS, CallsDateRangePreset, fmtRangeDate, presetRange, callsPresetRange,
} from '../dateRange';

type TabId = 'agents' | 'calls' | 'categories' | 'metrics' | 'insights' | 'timetracking' | 'settings';
const TABS: { id: TabId; label: string }[] = [
  { id: 'agents', label: 'Agents' },
  { id: 'calls', label: 'Calls' },
  { id: 'categories', label: 'Categories' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'insights', label: 'Insights' },
  { id: 'timetracking', label: 'Time Tracking' },
  { id: 'settings', label: 'Settings' },
];
// Supervisors (non-admin) see only the Calls and Metrics tabs — Agents, Categories, Insights,
// Time Tracking, and Settings are hidden entirely, not merely disabled. Insights is Admin-only
// (payroll-adjacent schedule/punch data), unlike Metrics which stays Supervisor-visible.
const SUPERVISOR_TAB_IDS: TabId[] = ['calls', 'metrics'];

// Rows per page for the Calls tab tables.
const CALLS_PAGE_SIZE = 25;

// All Calls Status filter (FR-2.4/OQ-2) — the only two values confirmed directly in code
// (Communication.InsertCall's default and CallEditForm's save), always offered regardless of
// what's currently loaded; any additional distinct Status value seen among the loaded calls is
// unioned in alongside these client-side, so an unanticipated value (e.g. whatever the Zoom
// ingestion path sets) is still selectable rather than silently unreachable via this filter.
const CONFIRMED_CALL_STATUSES = ['NeedsReview', 'Completed'];

// ── Business Hours (Settings tab, FR Group 4 — per-day, amended 2026-08-25) ─────────────────
// Weekday numbering matches the API/BusinessHoursDay contract: 1=Sunday..7=Saturday. Display
// order is Monday-first (reads more naturally for business hours) but is purely a rendering
// concern — the Weekday numbers sent to the API always come from each row's own `Weekday`
// field, never from display position, so reordering the UI can't desync from what's saved.
const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Sunday', 2: 'Monday', 3: 'Tuesday', 4: 'Wednesday', 5: 'Thursday', 6: 'Friday', 7: 'Saturday',
};
const WEEKDAY_ABBR: Record<number, string> = {
  1: 'Sun', 2: 'Mon', 3: 'Tue', 4: 'Wed', 5: 'Thu', 6: 'Fri', 7: 'Sat',
};
const WEEKDAY_DISPLAY_ORDER = [2, 3, 4, 5, 6, 7, 1];

// Local editable form shape for one day's row — `open` is a first-class flag (not just "both
// times filled") so unchecking it can deliberately clear the times (FR-4.2 — no defaults to
// fall back to) rather than leaving them as an ambiguous, still-populated-but-ignored pair.
interface BusinessHoursDayInput { Weekday: number; open: boolean; openTime: string; closeTime: string; }

// Every day starts closed — no invented placeholder hours (FR-4.2).
const emptyBusinessHoursInput = (): BusinessHoursDayInput[] =>
  Array.from({ length: 7 }, (_, i) => ({ Weekday: i + 1, open: false, openTime: '', closeTime: '' }));

const toBusinessHoursDayInput = (d: BusinessHoursDay): BusinessHoursDayInput => ({
  Weekday: d.Weekday,
  open: d.OpenEt != null && d.CloseEt != null,
  openTime: d.OpenEt ?? '',
  closeTime: d.CloseEt ?? '',
});
// Closed days always serialize to { OpenEt: null, CloseEt: null } — never a stale typed value
// left over from before the day was toggled off.
const fromBusinessHoursDayInput = (d: BusinessHoursDayInput): BusinessHoursDay => ({
  Weekday: d.Weekday,
  OpenEt: d.open ? d.openTime.trim() : null,
  CloseEt: d.open ? d.closeTime.trim() : null,
});
const businessHoursDayValid = (d: BusinessHoursDayInput): boolean =>
  !d.open || (d.openTime.trim() !== '' && d.closeTime.trim() !== '' && d.closeTime > d.openTime);
// "Mon 8:30–17:00, Sat 9:00–13:00" style summary, Monday-first, skipping closed days.
// '(not set)' when every day is closed (BusinessHoursConfigured === false).
const fmtBusinessHoursSummary = (days: BusinessHoursDay[]): string => {
  const openDays = WEEKDAY_DISPLAY_ORDER
    .map(w => days.find(d => d.Weekday === w))
    .filter((d): d is BusinessHoursDay => !!d && d.OpenEt != null && d.CloseEt != null);
  if (openDays.length === 0) return '(not set)';
  return openDays.map(d => `${WEEKDAY_ABBR[d.Weekday]} ${d.OpenEt}–${d.CloseEt}`).join(', ');
};

// Prev/Next pager shown under a paginated table; hidden when everything fits on one page.
const Pager: React.FC<{ page: number; total: number; pageSize: number; onPage: (p: number) => void }> = ({
  page, total, pageSize, onPage,
}) => {
  if (total <= pageSize) return null;
  const pages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-muted">
      <span>{from}–{to} of {total}</span>
      <div className="flex items-center gap-2">
        <button className={ui.btnSecondary} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
        <span>Page {page} of {pages}</span>
        <button className={ui.btnSecondary} disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

export const AdminPage: React.FC = () => {
  const isAdmin = useIsCommunicationAdmin();
  // Admin implies supervisor (mirrors the backend CommunicationSupervisor policy), so a full
  // Admin also satisfies isSupervisor. hasAccess below is written explicitly for readability.
  const isSupervisor = useIsCommunicationSupervisor();
  const hasAccess = isAdmin || isSupervisor;
  // Everyone lands on Metrics (per Ames 2026-08-10 — oversight-oriented for both roles).
  const [tab, setTab] = useState<TabId>('metrics');
  const visibleTabs = isAdmin ? TABS : TABS.filter(t => SUPERVISOR_TAB_IDS.includes(t.id));
  const [me, setMe] = useState<CurrentAgent | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [zoomUsers, setZoomUsers] = useState<ZoomUser[]>([]);
  const [jcsaEmployees, setJcsaEmployees] = useState<JcsaEmployee[]>([]);
  // Workforce.Roster names, for the Agents tab's Schedule Roster pairing column (Call Insights
  // Reports 2 & 3 identity mapping) — same list TimeTrackingAdmin's Roster section shows.
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);
  // BUG-1 fix (FR-2.4/OQ-2): an accumulator that only ever grows — every distinct `Status`
  // value seen in ANY successfully-fetched result set (filtered or not) is unioned in here and
  // never removed, so a value that exists somewhere in the system but happens to be absent from
  // the *currently* filtered `calls` state (e.g. after narrowing by Agent/Date Range/Account)
  // doesn't disappear from the Status dropdown. Deliberately not re-fetched unfiltered on its
  // own — that would double the 20s poll's network load (FR-"Performance"); it just rides along
  // with whatever fetch already happened (initial unfiltered load, filter changes, poll, manual
  // Refresh all go through refreshCalls below).
  const [seenCallStatuses, setSeenCallStatuses] = useState<string[]>([]);
  const [unmapped, setUnmapped] = useState<Call[]>([]);
  const [metrics, setMetrics] = useState<MissedCallMetrics | null>(null);

  // Metrics date-range filter. Admins and Supervisors default to today's single-day view.
  const [metricsPreset, setMetricsPreset] = useState<MetricsPreset>('current-day');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Effective range: preset-derived, or the custom fields once both are valid.
  const metricsRange = metricsPreset === 'custom'
    ? (customFrom && customTo && customFrom <= customTo ? { from: customFrom, to: customTo } : null)
    : presetRange(metricsPreset);

  // All Calls card filters (docs/requirements/admin-calls-filters-and-transcript.md, FR Group
  // 2) — deliberately independent state from metricsPreset/customFrom/customTo above (OQ-4):
  // this is a different tab's control over a different, unrelated view, and defaults to "All"
  // (no date constraint) rather than Metrics' "must always have some range" default, so the
  // card's first-load appearance is unchanged from today (FR-2.1).
  const [callsDateRangePreset, setCallsDateRangePreset] = useState<CallsDateRangePreset>('all');
  const [callsCustomFrom, setCallsCustomFrom] = useState('');
  const [callsCustomTo, setCallsCustomTo] = useState('');
  // '' = All Agents, 'unassigned' = Unassigned (-> unassignedOnly), else String(AgentID).
  const [callsAgentSelection, setCallsAgentSelection] = useState('');
  // The applied (debounced) Account filter value — see AllCallsFilterBar for the raw-typed text.
  const [callsAccountNumber, setCallsAccountNumber] = useState('');
  const [callsStatus, setCallsStatus] = useState('');
  // True while the very first (or a just-changed-filter) fetch is in flight (FR-4.4) — the
  // 20s poll and manual Refresh button deliberately don't toggle this (see refreshCalls below),
  // so a routine background refresh never flashes a spinner over already-loaded rows.
  const [callsLoading, setCallsLoading] = useState(true);
  // Dismissible (FR-4.5) — cleared on dismiss or the next successful fetch; previously-loaded
  // rows and the filter controls stay visible/interactive while this is set.
  const [callsError, setCallsError] = useState<string | null>(null);
  // Admin-only (FR-1.3/OQ-1) View Transcript/Details modal — see the Actions column below.
  const [transcriptViewerCallId, setTranscriptViewerCallId] = useState<number | null>(null);

  // Effective Calls-tab date range: preset-derived (including 'all' -> no constraint), or the
  // custom fields once both are valid — same shape/convention as metricsRange above.
  const callsDateRange = callsDateRangePreset === 'custom'
    ? (callsCustomFrom && callsCustomTo && callsCustomFrom <= callsCustomTo ? { from: callsCustomFrom, to: callsCustomTo } : null)
    : callsPresetRange(callsDateRangePreset);
  // True only while Custom is selected but incomplete/invalid — mirrors metricsRange's null
  // case (FR-4.7/TC-59): no fetch is issued in this state, rather than silently falling back
  // to an unconstrained range.
  const callsDateRangeIncomplete = callsDateRangePreset === 'custom' && !callsDateRange;

  // The filter object actually sent to getAllCalls (FR-3.6) — memoized so its identity only
  // changes when a value that matters actually changes, which both the mount/filter-change
  // effect and refreshActiveTab's useCallback below depend on.
  const callsFilterParams: CallsFilter = useMemo(() => ({
    status: callsStatus || undefined,
    from: callsDateRange?.from,
    to: callsDateRange?.to,
    agentId: callsAgentSelection !== '' && callsAgentSelection !== 'unassigned' ? Number(callsAgentSelection) : undefined,
    unassignedOnly: callsAgentSelection === 'unassigned' ? true : undefined,
    accountNumber: callsAccountNumber || undefined,
  }), [callsStatus, callsDateRange?.from, callsDateRange?.to, callsAgentSelection, callsAccountNumber]);

  // FR-2.4/OQ-2 — always the two confirmed values (in their fixed order), unioned with whatever
  // additional distinct Status strings have ever been seen across loaded fetches (in first-seen
  // order), NOT the live/possibly-filtered `calls` state (BUG-1 — that shrank/changed this list
  // whenever an unrelated filter narrowed the currently-displayed rows).
  const callStatusOptions = [
    ...CONFIRMED_CALL_STATUSES,
    ...seenCallStatuses.filter(s => !CONFIRMED_CALL_STATUSES.includes(s)),
  ];

  // Whether any of the four filters is off its default — drives the filter-aware empty-state
  // copy (FR-4.3) so "no calls match this filter" never reads the same as "no calls exist".
  const hasActiveCallsFilter =
    callsDateRangePreset !== 'all' || callsAgentSelection !== '' || callsAccountNumber !== '' || callsStatus !== '';

  // Per-row state for unassigned call assignment
  const [assignSelections, setAssignSelections] = useState<Record<number, number>>({});

  // Current page for the Calls tab tables (client-side pagination over the already-loaded lists).
  const [callsPage, setCallsPage] = useState(1);
  const [unmappedPage, setUnmappedPage] = useState(1);

  // Staged Zoom-pairing / JCSA-employee / Schedule-Roster edits for the Agents table. Dropdown
  // changes are held here (not persisted) until the user clicks Save, which writes every
  // changed row at once.
  const [pendingAgentEdits, setPendingAgentEdits] = useState<Record<number, { zoom?: string; jcsa?: string; roster?: string }>>({});
  const [savingAgents, setSavingAgents] = useState(false);
  // Server rejection from the staged-edit save (e.g. duplicate roster mapping 400) — shown
  // beside the Save controls; cleared on the next save attempt or discard.
  const [agentSaveError, setAgentSaveError] = useState<string | null>(null);

  // Dev simulator state
  const [simCallerNumber, setSimCallerNumber] = useState('');
  const [simAgentId, setSimAgentId] = useState<number | ''>('');
  const [simMissed, setSimMissed] = useState(false);
  const [simBusy, setSimBusy] = useState(false);

  // Settings state
  const [settings, setSettings] = useState<CommunicationSettings | null>(null);
  const [extensionInput, setExtensionInput] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<ZoomQueuePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // Business hours settings (FR Group 4, per-day) — its own independent form/save action,
  // separate from the queue-extension save above, so saving one never touches the other's
  // fields. Always exactly 7 rows, Weekday 1..7, kept in that order internally regardless of
  // display order so a save always sends a well-formed full-week replacement.
  const [businessHoursInput, setBusinessHoursInput] = useState<BusinessHoursDayInput[]>(emptyBusinessHoursInput());
  const [businessHoursSaving, setBusinessHoursSaving] = useState(false);
  const [businessHoursMsg, setBusinessHoursMsg] = useState<string | null>(null);

  const refreshAgents = () => getAgents().then(setAgents);
  const refreshZoomUsers = () => getZoomUsers().then(setZoomUsers);
  const refreshJcsaEmployees = () => getJcsaEmployees().then(setJcsaEmployees);
  const refreshRoster = () => getTimeTrackingRoster().then(setRoster);
  // Always re-issues with whatever filter is currently selected (FR-4.1) — used by the 20s
  // poll and the manual Refresh button. On failure, keeps whatever rows/filter were already
  // showing and surfaces a dismissible error instead (FR-4.5) rather than clearing the card.
  const refreshCalls = async () => {
    try {
      const data = await getAllCalls(callsFilterParams);
      setCalls(data);
      // BUG-1 fix — grow (never shrink) the seen-statuses accumulator from this fetch's rows,
      // whether this was the unfiltered initial load, a filter-narrowed fetch, a poll tick, or a
      // manual Refresh; see seenCallStatuses' declaration above for why.
      setSeenCallStatuses(prev => {
        const next = [...prev];
        for (const c of data) {
          if (c.Status && !next.includes(c.Status)) next.push(c.Status);
        }
        return next;
      });
      setCallsError(null);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setCallsError(axiosErr?.response?.data?.error ?? 'Could not load calls — try again.');
    }
  };
  const refreshUnmapped = () => getUnmappedCalls().then(setUnmapped);
  // No-op while Custom is selected but incomplete — keeps the last-loaded data on screen.
  const refreshMetrics = () => metricsRange
    ? getMissedMetrics(metricsRange.from, metricsRange.to).then(setMetrics)
    : Promise.resolve();

  const loadSettings = () => getSettings().then(s => {
    setSettings(s);
    setExtensionInput(s.ZoomCallQueueExtension ?? '');
    setBusinessHoursInput(s.BusinessHours.map(toBusinessHoursDayInput));
  });

  const [refreshing, setRefreshing] = useState(false);

  // Role-aware mount fetch (FR-13): Calls-tab and Metrics-tab data loads for both Admins and
  // Supervisors (both satisfy the backend CommunicationSupervisor policy). GET admin/agents is
  // also CommunicationSupervisor (roster read, shared so the Assign-To / Dev Simulator agent
  // pickers work identically for both roles) — the write-oriented agent endpoints it feeds
  // (pairing, deactivate, etc.) stay CommunicationAdmin-only and are only exercised from the
  // Agents tab, which stays hidden for Supervisors. Zoom-users/JCSA-employees/Settings/
  // Categories stay CommunicationAdmin-only on the backend, so they're only fetched for
  // Admins — calling them for a Supervisor session would 403 on every page load.
  useEffect(() => {
    if (!hasAccess) return;
    getMe().then(setMe);
    refreshUnmapped();
    refreshAgents();
    if (isAdmin) {
      refreshZoomUsers();
      refreshJcsaEmployees();
      refreshRoster();
      getCategories().then(setCategories);
      loadSettings();
    }
  }, [hasAccess, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Metrics load and re-load whenever the selected date range changes (preset switch,
  // or a completed pair of custom dates). Also covers the initial load.
  useEffect(() => {
    if (!hasAccess || !metricsRange) return;
    refreshMetrics();
  }, [hasAccess, metricsRange?.from, metricsRange?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calls tab filter-triggered fetch (FR Group 2/4) — fires on mount (covering the initial,
  // unfiltered load) and again whenever any of the four filters changes. Resets to page 1 each
  // time (FR-4.2) and is the one path that shows the loading indicator (FR-4.4): the 20s poll
  // and manual Refresh button call refreshCalls directly (see refreshActiveTab below) and
  // deliberately skip this effect, so a routine background refresh never flashes a spinner over
  // rows that are already on screen.
  useEffect(() => {
    if (!hasAccess) return;
    if (callsDateRangeIncomplete) return; // no-op while Custom is incomplete, mirrors metricsRange
    setCallsPage(1);
    setCallsLoading(true);
    refreshCalls().finally(() => setCallsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, callsDateRangeIncomplete, callsFilterParams]);

  // Re-fetch the data behind the currently-selected tab. Used by both the auto-refresh
  // poll and the manual Refresh button, so the two never drift apart.
  const refreshActiveTab = useCallback(async () => {
    switch (tab) {
      case 'calls': await Promise.all([refreshCalls(), refreshUnmapped()]); break;
      case 'metrics': await refreshMetrics(); break;
      case 'agents': await Promise.all([refreshAgents(), refreshZoomUsers(), refreshRoster()]); break;
      default: break;
    }
    // callsFilterParams is included so a filter change also regenerates this callback,
    // capturing a refreshCalls closure bound to the new filter (FR-4.1's "poll never reverts
    // to the unfiltered view" — otherwise a memoized refreshActiveTab could keep calling a
    // stale refreshCalls closed over an earlier filter).
  }, [tab, metricsRange?.from, metricsRange?.to, callsFilterParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try { await refreshActiveTab(); } finally { setRefreshing(false); }
  };

  // Auto-refresh the live tabs (Calls, Metrics) on an interval so ingested Zoom calls
  // surface without leaving the page. Static tabs (Categories, Settings) don't poll.
  useEffect(() => {
    if (!hasAccess || (tab !== 'calls' && tab !== 'metrics')) return;
    const id = setInterval(() => { refreshActiveTab(); }, POLL_MS);
    return () => clearInterval(id);
  }, [hasAccess, tab, refreshActiveTab]);

  // Keep the current page in range when a table's row count shrinks (e.g. a call gets assigned).
  useEffect(() => {
    setCallsPage(p => Math.min(p, Math.max(1, Math.ceil(calls.length / CALLS_PAGE_SIZE))));
  }, [calls.length]);
  useEffect(() => {
    setUnmappedPage(p => Math.min(p, Math.max(1, Math.ceil(unmapped.length / CALLS_PAGE_SIZE))));
  }, [unmapped.length]);

  if (!hasAccess) return <Navigate to="/communication" replace />;

  const navItems = [
    { path: '/communication', label: 'My Calls' },
    { path: '/communication/admin', label: 'Admin' },
  ];
  const activeAgents = agents.filter(a => a.IsActive);

  // Current page slices for the Calls tab tables.
  const pagedCalls = calls.slice((callsPage - 1) * CALLS_PAGE_SIZE, callsPage * CALLS_PAGE_SIZE);
  const pagedUnmapped = unmapped.slice((unmappedPage - 1) * CALLS_PAGE_SIZE, unmappedPage * CALLS_PAGE_SIZE);

  // The persisted select value for each column. Zoom: the paired user id ('' = unassigned).
  // JCSA: '' when explicitly unassigned, otherwise the manual override or the email auto-match.
  const currentZoomVal = (a: Agent) => a.ZoomUserId ?? '';
  const currentJcsaVal = (a: Agent) =>
    a.JcsaEmployeeUnassigned ? '' : String(a.JcsaEmployeeID ?? a.MatchedJcsaEmployeeID ?? '');
  // SAVED value only — the auto-match suggestion deliberately excluded. Folding the
  // suggestion in here made suggestion-only rows compare as "not dirty", so Save never
  // persisted them and the row looked mapped while the DB column stayed null.
  const currentRosterVal = (a: Agent) => a.RosterCanonicalName ?? '';
  // What the dropdown shows when nothing is staged: the saved value, else the auto-match
  // suggestion. A suggestion-only row therefore counts as a pending change (dirty vs the
  // saved value) and the Save button persists it — accepting suggestions is one click.
  const displayRosterVal = (a: Agent) => a.RosterCanonicalName ?? a.MatchedRosterName ?? '';

  // The value shown in each dropdown: the staged edit if the row has one, else the saved value.
  const effectiveZoomVal = (a: Agent) => pendingAgentEdits[a.AgentID]?.zoom ?? currentZoomVal(a);
  const effectiveJcsaVal = (a: Agent) => pendingAgentEdits[a.AgentID]?.jcsa ?? currentJcsaVal(a);
  const effectiveRosterVal = (a: Agent) => pendingAgentEdits[a.AgentID]?.roster ?? displayRosterVal(a);

  const zoomDirty = (a: Agent) => effectiveZoomVal(a) !== currentZoomVal(a);
  const jcsaDirty = (a: Agent) => effectiveJcsaVal(a) !== currentJcsaVal(a);
  const rosterDirty = (a: Agent) => effectiveRosterVal(a) !== currentRosterVal(a);
  const agentDirty = (a: Agent) => zoomDirty(a) || jcsaDirty(a) || rosterDirty(a);
  const pendingAgentCount = agents.filter(agentDirty).length;

  // Stage a dropdown change without persisting it; the Save button writes all staged rows.
  const stageZoom = (agent: Agent, value: string) =>
    setPendingAgentEdits(prev => ({ ...prev, [agent.AgentID]: { ...prev[agent.AgentID], zoom: value } }));
  const stageJcsa = (agent: Agent, value: string) =>
    setPendingAgentEdits(prev => ({ ...prev, [agent.AgentID]: { ...prev[agent.AgentID], jcsa: value } }));
  const stageRoster = (agent: Agent, value: string) =>
    setPendingAgentEdits(prev => ({ ...prev, [agent.AgentID]: { ...prev[agent.AgentID], roster: value } }));

  const handleDiscardAgentEdits = () => { setPendingAgentEdits({}); setAgentSaveError(null); };

  // Persist every changed row in one go: unpair/pair Zoom and set/unassign the JCSA employee
  // for each dirty column, then clear the staged edits and refresh.
  const handleSaveAgents = async () => {
    const ops: Promise<unknown>[] = [];
    for (const a of agents) {
      if (zoomDirty(a)) {
        const userId = effectiveZoomVal(a);
        if (userId === '') ops.push(setAgentZoom(a.AgentID, {}));
        else {
          const zoom = zoomUsers.find(z => z.UserId === userId);
          if (zoom) ops.push(setAgentZoom(a.AgentID, { ZoomUserId: zoom.UserId, ZoomEmail: zoom.Email, ZoomDisplayName: zoom.DisplayName }));
        }
      }
      if (jcsaDirty(a)) {
        const v = effectiveJcsaVal(a);
        if (v === '') ops.push(setAgentJcsaEmployee(a.AgentID, null, true));   // explicit unassign
        else ops.push(setAgentJcsaEmployee(a.AgentID, Number(v), false));      // manual override
      }
      if (rosterDirty(a)) {
        const v = effectiveRosterVal(a);
        ops.push(setAgentRoster(a.AgentID, v === '' ? null : v));
      }
    }
    if (ops.length === 0) return;
    setSavingAgents(true);
    setAgentSaveError(null);
    try {
      await Promise.all(ops);
      setPendingAgentEdits({});
      await Promise.all([refreshAgents(), refreshZoomUsers(), refreshRoster()]);
    } catch (err) {
      // Surface server rejections (e.g. duplicate roster mapping 400) instead of failing
      // silently; staged edits are kept so the user can correct and re-save. Rows whose ops
      // DID succeed are reconciled by the refresh below.
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setAgentSaveError(axiosErr?.response?.data?.error ?? 'Save failed — one or more changes were rejected. Fix the highlighted rows and try again.');
      await Promise.all([refreshAgents(), refreshZoomUsers(), refreshRoster()]).catch(() => undefined);
    } finally {
      setSavingAgents(false);
    }
  };

  // Resets all four All Calls filters to their defaults in one action (FR-2.6). The
  // callsFilterParams memo recomputes from these resets, which the effect above picks up to
  // reset callsPage to 1 and re-fetch — no separate fetch call needed here.
  const handleClearCallsFilters = () => {
    setCallsDateRangePreset('all');
    setCallsCustomFrom('');
    setCallsCustomTo('');
    setCallsAgentSelection('');
    setCallsAccountNumber('');
    setCallsStatus('');
  };

  const handleAssignCall = async (call: Call) => {
    const agentId = assignSelections[call.CallID];
    if (!agentId) return;
    await assignCall(call.CallID, agentId);
    await Promise.all([refreshUnmapped(), refreshCalls()]);
    setAssignSelections(prev => { const next = { ...prev }; delete next[call.CallID]; return next; });
  };

  const handleSimulate = async () => {
    setSimBusy(true);
    try {
      const selectedAgent = simAgentId !== '' ? agents.find(a => a.AgentID === simAgentId) : undefined;
      await simulateCall({
        CallerNumber: simCallerNumber || undefined,
        ZoomUserId: selectedAgent?.ZoomUserId || undefined,
        Missed: simMissed,
      });
      await Promise.all([refreshUnmapped(), refreshCalls(), refreshMetrics()]);
    } finally {
      setSimBusy(false);
    }
  };

  const handleVerifyExtension = async () => {
    const ext = extensionInput.trim();
    if (!ext) return;
    setPreviewing(true);
    setPreview(null);
    setPreviewError(null);
    try {
      setPreview(await previewZoomQueue(ext));
    } catch (e: any) {
      setPreviewError(e?.response?.data?.error ?? 'Could not reach Zoom to verify the extension.');
    } finally {
      setPreviewing(false);
    }
  };

  // BUG-5 fix: the backend's BUG-1 contract distinguishes "key omitted from the request body"
  // (leave the extension unchanged — what a Business-Hours-only save relies on) from "key sent
  // as an explicit empty string" (clear the override) — and JSON.stringify/axios silently DROP
  // any key whose value is `undefined`, so `extensionInput.trim() || undefined` collapsed a
  // deliberate blank-and-save into an omitted key, making it indistinguishable from "don't
  // touch this field." This form has no "don't touch" case of its own — every click of its
  // Save button is a deliberate value, including deliberately blank — so it must always send
  // the trimmed string explicitly, never `undefined`.
  //
  // BUG-7 fix: "dirty" can't be text-only. The displayed value is the *effective* value
  // regardless of source (settings.ZoomCallQueueExtensionSource is 'Database' | 'Config' |
  // 'Unset') — when it's currently Config-sourced (no DB row yet), an admin re-typing the exact
  // same text to explicitly pin it into the database is a real, meaningful Save (it creates a
  // DB row and attributes UpdatedBy) even though the string never changed. So a non-blank value
  // that isn't yet Database-sourced also counts as dirty. A blank value with no DB row (Unset)
  // stays inert on its own — there's nothing to "confirm" about persisting an override for a
  // value that's already effectively empty and un-persisted.
  const extensionDirty =
    extensionInput.trim() !== (settings?.ZoomCallQueueExtension ?? '') ||
    (extensionInput.trim() !== '' && settings?.ZoomCallQueueExtensionSource !== 'Database');

  const handleSaveSettings = async () => {
    if (!extensionDirty) return; // nothing to actually transmit — keep "Saved." honest
    setSettingsSaving(true);
    setSettingsMsg(null);
    try {
      const updated = await updateSettings({ ZoomCallQueueExtension: extensionInput.trim() });
      setSettings(updated);
      setExtensionInput(updated.ZoomCallQueueExtension ?? '');
      setSettingsMsg('Saved.');
    } catch {
      setSettingsMsg('Save failed.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Business hours (FR Group 4, per-day) — Save always sends the full 7-day array as a
  // complete replacement (never a partial patch), so there's no scenario where the server
  // receives a half-week update. Saving refreshes Metrics too (when a range is already
  // loaded) so the Overflow/Abandon-Rate cards reflect the new week immediately, matching
  // FR-4.1's "live-effective on save, no restart" behavior.
  const businessHoursValid = businessHoursInput.every(businessHoursDayValid);
  const businessHoursDirty = !settings ? false : businessHoursInput.some((d, i) => {
    const saved = settings.BusinessHours[i];
    const current = fromBusinessHoursDayInput(d);
    return !saved || saved.Weekday !== current.Weekday || saved.OpenEt !== current.OpenEt || saved.CloseEt !== current.CloseEt;
  });

  const updateBusinessHoursDay = (weekday: number, patch: Partial<BusinessHoursDayInput>) => {
    setBusinessHoursInput(prev => prev.map(d => (d.Weekday === weekday ? { ...d, ...patch } : d)));
    setBusinessHoursMsg(null);
  };
  // Turning a day off deliberately clears its times (FR-4.2) rather than leaving a stale,
  // now-ignored pair behind — re-opening the day always starts blank again, never a
  // remembered/invented default.
  const toggleBusinessHoursDayOpen = (weekday: number, open: boolean) =>
    updateBusinessHoursDay(weekday, open ? { open: true } : { open: false, openTime: '', closeTime: '' });
  // Optional ergonomic affordance: copy one (already-open) day's window onto every other day,
  // so setting the same hours across the week doesn't require re-typing them seven times.
  // Disabled from the UI for a closed source day (nothing meaningful to propagate).
  const copyBusinessHoursDayToAll = (source: BusinessHoursDayInput) => {
    setBusinessHoursInput(prev => prev.map(d => (d.Weekday === source.Weekday
      ? d
      : { ...d, open: source.open, openTime: source.openTime, closeTime: source.closeTime })));
    setBusinessHoursMsg(null);
  };

  const handleSaveBusinessHours = async () => {
    if (!businessHoursValid) return;
    setBusinessHoursSaving(true);
    setBusinessHoursMsg(null);
    try {
      const updated = await updateSettings({ BusinessHours: businessHoursInput.map(fromBusinessHoursDayInput) });
      setSettings(updated);
      setBusinessHoursInput(updated.BusinessHours.map(toBusinessHoursDayInput));
      setBusinessHoursMsg('Saved.');
      await refreshMetrics();
    } catch (err) {
      // Surface the server's 400 message verbatim (malformed time, close <= open — named to
      // the offending weekday by the backend, FR-4.5), same extraction the Agents-tab save uses.
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setBusinessHoursMsg(axiosErr?.response?.data?.error ?? 'Save failed.');
    } finally {
      setBusinessHoursSaving(false);
    }
  };

  // Full 0–23 hour axis so the chart reads as a day even when only a few hours have misses.
  // Labels are 12-hour AM/PM, compact ("12a" … "11p") so all 24 ticks fit the axis.
  const fmtHour12 = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`;
  const hourData = Array.from({ length: 24 }, (_, h) => ({
    hour: fmtHour12(h),
    missed: metrics?.MissedByHour.find(x => x.Hour === h)?.MissedCount ?? 0,
  }));
  // "3:50" from seconds; dash while durations are NULL upstream (R-A1).
  const fmtDur = (seconds?: number | null) => {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const s = Math.round(seconds);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const agentData = (metrics?.PerAgent ?? []).map(a => ({
    name: a.DisplayName ?? String(a.AgentID),
    answered: a.AnsweredCount,
    avgDur: a.AvgDurationSeconds ?? null,
  }));
  // Per-day answered (green) stacked under missed (red). Dates are US Eastern calendar
  // days from the API, serialized as date-only midnights — label them with timeZone:'UTC'
  // so the viewer's own timezone can't shift the bucket to a neighboring day.
  const dayData = (metrics?.ByDay ?? []).map(d => ({
    day: new Date(d.Date).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' }),
    answered: d.AnsweredCount,
    missed: d.MissedCount,
  }));
  // Abandon Rate (FR-3.5) — business-hours-scoped, computed client-side from the raw counts
  // the backend returns (deliberately not a precomputed rate — see the contract). Three
  // distinct states, so "not configured" (FR-3.6) is never confused with the ordinary
  // zero-denominator dash: 'not-configured' before an admin sets business hours (never a
  // silent fallback to the old unscoped all-hours formula — that would reintroduce the exact
  // after-hours skew this feature removes); 'no-data' when hours are configured but no
  // business-hours calls fell in range; 'value' otherwise.
  type AbandonRateResult = { kind: 'not-configured' } | { kind: 'no-data' } | { kind: 'value'; pct: number };
  const abandonRateResult: AbandonRateResult = (() => {
    if (!metrics || !metrics.BusinessHoursConfigured) return { kind: 'not-configured' };
    const bhAnswered = metrics.BusinessHoursAnsweredCount ?? 0;
    const bhMissed = metrics.BusinessHoursMissedCount ?? 0;
    const total = bhAnswered + bhMissed;
    return total === 0 ? { kind: 'no-data' } : { kind: 'value', pct: Math.round((bhMissed / total) * 100) };
  })();

  // Overflow coverage (FR-2.4) — the share of calls in range whose raw Zoom CallResult has
  // been sweep-reconciled. Independent of BusinessHoursConfigured (FR-2.6 vs. FR-2.4 are
  // distinct, co-occurrence-able reasons a number might be incomplete): a partially-swept
  // range under-reports the two Overflow cards even once business hours are configured.
  const overflowTotalCalls = metrics ? metrics.AnsweredCount + metrics.MissedCount : 0;
  const overflowCoveragePct = metrics && overflowTotalCalls > 0
    ? Math.round((metrics.OverflowCoveredCount / overflowTotalCalls) * 100)
    : null;
  const overflowCoverageIncomplete = overflowCoveragePct !== null && overflowCoveragePct < 100;

  // Category usage in the selected range. Counts sum to category assignments
  // (a call can carry multiple categories). The pie shows each category's share.
  const categoryData = (metrics?.ByCategory ?? []).map(c => ({
    name: `${c.Code} — ${c.Name}`,
    code: c.Code,
    count: c.CallCount,
  }));
  const categoryTotal = categoryData.reduce((sum, c) => sum + c.count, 0);
  const categoryPct = (count: number) =>
    categoryTotal === 0 ? '0%' : `${Math.round((count / categoryTotal) * 100)}%`;

  const axisTick = { fill: '#94a3b8', fontSize: 12 };
  const CATEGORY_COLORS = [
    '#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed',
    '#0891b2', '#db2777', '#65a30d', '#9333ea', '#ea580c',
    '#0d9488', '#4f46e5',
  ];

  return (
    <PageLayout navItems={navItems}>
      <h1 className={`${ui.pageTitle} mb-6`}>Communication — Admin</h1>

      <div className="mb-6 flex items-center justify-between gap-3 border-b border-border-default">
        <div className="flex gap-1">
          {visibleTabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id ? 'border-blue-600 text-primary' : 'border-transparent text-muted hover:text-secondary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {(tab === 'calls' || tab === 'metrics') && (
          <button className={`${ui.btnSecondary} mb-1`} onClick={handleManualRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* ── Agents ─────────────────────────────────────────── */}
        {tab === 'agents' && (
          <section className={ui.sectionCard}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className={ui.sectionTitle}>Agents</h2>
              <div className="flex items-center gap-3">
                {pendingAgentCount > 0 && (
                  <span className="text-sm text-muted">
                    {pendingAgentCount} unsaved {pendingAgentCount === 1 ? 'row' : 'rows'}
                  </span>
                )}
                <button
                  className={ui.btnSecondary}
                  onClick={handleDiscardAgentEdits}
                  disabled={savingAgents || pendingAgentCount === 0}
                >
                  Discard
                </button>
                <button
                  className={ui.btnPrimary}
                  onClick={handleSaveAgents}
                  disabled={savingAgents || pendingAgentCount === 0}
                >
                  {savingAgents ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
            {agentSaveError && (
              <div role="alert" className="mb-3 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
                {agentSaveError}
              </div>
            )}
            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead className={ui.thead}>
                  <tr>
                    <th className={ui.th}>Name (Email)</th>
                    <th className={ui.th}>Zoom Pairing</th>
                    <th className={ui.th}>JCSA Employee</th>
                    <th className={ui.th}>Schedule Roster</th>
                    <th className={ui.th}>Active</th>
                    <th className={`${ui.th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={ui.tbody}>
                  {(() => {
                    // A Zoom user is taken if any row (staged edits included) is pointing at it,
                    // so a user paired in one row disappears from the others' lists. A roster
                    // name is taken the same way (BUG-2) — two agents must never both claim the
                    // same Workforce.Roster identity, since Reports 2/3 join schedule/punch data
                    // by that name and a duplicate would surface the same rows under both agents.
                    const takenZoom = new Set(agents.map(effectiveZoomVal).filter(v => v !== ''));
                    const takenRoster = new Set(agents.map(effectiveRosterVal).filter(v => v !== ''));
                    return agents.map(a => {
                    const rowZoom = effectiveZoomVal(a);
                    const rowJcsa = effectiveJcsaVal(a);
                    const rowRoster = effectiveRosterVal(a);
                    const dirtyRing = ' border-amber-400 ring-1 ring-amber-400';
                    return (
                      <tr key={a.AgentID} className="hover:bg-surface-hover">
                        <td className={ui.td}>{a.DisplayName ?? a.AzureADObjectID}{a.Email ? ` (${a.Email})` : ''}</td>
                        <td className={ui.td}>
                          <select
                            className={ui.control + (zoomDirty(a) ? dirtyRing : '')}
                            value={rowZoom}
                            onChange={e => stageZoom(a, e.target.value)}
                          >
                            <option value="">— Unassigned —</option>
                            {rowZoom !== '' && !zoomUsers.some(z => z.UserId === rowZoom) && (
                              <option value={rowZoom}>
                                {a.ZoomDisplayName ?? rowZoom}{a.ZoomEmail ? ` (${a.ZoomEmail})` : ''}
                              </option>
                            )}
                            {zoomUsers.filter(z => z.UserId === rowZoom || !takenZoom.has(z.UserId)).map(z => (
                              <option key={z.UserId} value={z.UserId}>
                                {z.DisplayName ?? z.UserId}{z.Email ? ` (${z.Email})` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={ui.td}>
                          <select
                            className={ui.control + (jcsaDirty(a) ? dirtyRing : '')}
                            value={rowJcsa}
                            onChange={e => stageJcsa(a, e.target.value)}
                          >
                            <option value="">— Unassigned —</option>
                            {jcsaEmployees.map(emp => (
                              <option key={emp.EmployeeID} value={emp.EmployeeID}>
                                {emp.Name || `Employee #${emp.EmployeeID}`}{emp.Email ? ` (${emp.Email})` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={ui.td}>
                          {/* Workforce.Roster identity mapping for Call Insights Reports 2 & 3
                              (schedule/punch join) — mirrors the JCSA Employee column: an
                              auto-match suggestion (MatchedRosterName) shown as the default
                              value, with a manual override staged like every other column here.
                              A name already claimed by another agent (saved or staged) is
                              excluded here, same as the Zoom Pairing column's takenZoom (BUG-2) —
                              this row's own current/staged value stays selectable. */}
                          <select
                            className={ui.control + (rosterDirty(a) ? dirtyRing : '')}
                            value={rowRoster}
                            onChange={e => stageRoster(a, e.target.value)}
                          >
                            <option value="">— Unassigned —</option>
                            {rowRoster !== '' && !roster.some(r => r.CanonicalName === rowRoster) && (
                              <option value={rowRoster}>{rowRoster} (not in roster)</option>
                            )}
                            {roster.filter(r => r.IsActive).filter(r => r.CanonicalName === rowRoster || !takenRoster.has(r.CanonicalName)).map(r => (
                              <option key={r.RosterID} value={r.CanonicalName}>{r.CanonicalName}</option>
                            ))}
                          </select>
                        </td>
                        <td className={ui.td}>{a.IsActive ? 'Yes' : 'No'}</td>
                        <td className={`${ui.td} text-right`}>
                          {a.IsActive ? (
                            <button className={ui.btnDanger} onClick={async () => { await deactivateAgent(a.AgentID); await refreshAgents(); }}>
                              Deactivate
                            </button>
                          ) : (
                            <button className={ui.btnSecondary} onClick={async () => { await reactivateAgent(a.AgentID); await refreshAgents(); }}>
                              Reactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  });
                  })()}
                  {agents.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">No agents yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Calls (all + unassigned + dev simulator) ─────────── */}
        {tab === 'calls' && (
          <>
            <section className={ui.sectionCard}>
              <h2 className={`${ui.sectionTitle} mb-4`}>All Calls</h2>
              <AllCallsFilterBar
                dateRangePreset={callsDateRangePreset}
                onDateRangePresetChange={setCallsDateRangePreset}
                customFrom={callsCustomFrom}
                onCustomFromChange={setCallsCustomFrom}
                customTo={callsCustomTo}
                onCustomToChange={setCallsCustomTo}
                agentSelection={callsAgentSelection}
                onAgentSelectionChange={setCallsAgentSelection}
                agents={agents}
                accountNumber={callsAccountNumber}
                onAccountNumberChange={setCallsAccountNumber}
                status={callsStatus}
                onStatusChange={setCallsStatus}
                statusOptions={callStatusOptions}
                onClearFilters={handleClearCallsFilters}
              />
              {callsError && (
                <div
                  role="alert"
                  className="mb-3 flex items-start justify-between gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
                >
                  <span>{callsError}</span>
                  <button
                    type="button"
                    className="shrink-0 font-medium underline"
                    onClick={() => setCallsError(null)}
                    aria-label="Dismiss error"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {/* aria-live so the loading state is exposed to screen readers, not just a visual
                  spinner (accessibility NFR). */}
              <div aria-live="polite">
                {callsLoading ? (
                  <LoadingSpinner size="sm" message="Loading calls…" />
                ) : (
                  <>
                    <div className={ui.tableWrap}>
                      <table className={ui.table}>
                        <thead className={ui.thead}>
                          <tr>
                            <th className={ui.th}>Time</th>
                            <th className={ui.th}>Agent</th>
                            <th className={ui.th}>Account</th>
                            <th className={ui.th}>Status</th>
                            {/* FR-1.3/OQ-1 — Admin-only; Supervisors never see this column. */}
                            {isAdmin && <th className={`${ui.th} text-right`}>Actions</th>}
                          </tr>
                        </thead>
                        <tbody className={ui.tbody}>
                          {pagedCalls.map(c => (
                            <tr key={c.CallID} className="hover:bg-surface-hover">
                              <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeLocal(c.CallTimeUtc)}</td>
                              <td className={ui.td}>{c.AgentName ?? (c.AgentID ? `#${c.AgentID}` : <span className="text-muted">—</span>)}</td>
                              <td className={ui.td}>{c.AccountNumber || <span className="text-muted">—</span>}</td>
                              <td className={ui.td}><StatusBadge status={c.Status} /></td>
                              {isAdmin && (
                                <td className={`${ui.td} text-right`}>
                                  {/* FR-1.1/OQ-7 — label mirrors TranscriptRetrievalPanel's exact
                                      convention; FR-1.5/FR-4.6 — unaffected by any filter state. */}
                                  <button
                                    type="button"
                                    className={ui.btnSecondary}
                                    onClick={() => setTranscriptViewerCallId(c.CallID)}
                                  >
                                    {c.TranscriptFetchStatus === 'Fetched' ? 'View transcript' : 'View details'}
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                          {calls.length === 0 && (
                            <tr>
                              <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-sm text-muted">
                                {/* FR-4.3 — filter-aware empty-state copy, distinct from the
                                    genuine "nothing in the system" case. */}
                                {hasActiveCallsFilter ? 'No calls match the selected filters.' : 'No calls yet.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <Pager page={callsPage} total={calls.length} pageSize={CALLS_PAGE_SIZE} onPage={setCallsPage} />
                  </>
                )}
              </div>
            </section>

            {isAdmin && transcriptViewerCallId !== null && (
              <TranscriptViewer
                callId={transcriptViewerCallId}
                categories={categories}
                onClose={() => setTranscriptViewerCallId(null)}
              />
            )}

            <section className={ui.sectionCard}>
              <h2 className={`${ui.sectionTitle} mb-1`}>Unassigned Calls ({unmapped.length})</h2>
              <p className="mb-4 text-sm text-muted">Calls whose Zoom user has no agent mapping.</p>
              <div className={ui.tableWrap}>
                <table className={ui.table}>
                  <thead className={ui.thead}>
                    <tr><th className={ui.th}>Time</th><th className={ui.th}>Caller</th><th className={ui.th}>Account</th><th className={ui.th}>ZoomCallId</th><th className={ui.th}>Assign To</th></tr>
                  </thead>
                  <tbody className={ui.tbody}>
                    {pagedUnmapped.map(c => (
                      <tr key={c.CallID} className="hover:bg-surface-hover">
                        <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeLocal(c.CallTimeUtc)}</td>
                        <td className={ui.td}>{c.CallerNumber ? formatPhoneNumber(c.CallerNumber) : <span className="text-muted">—</span>}</td>
                        <td className={ui.td}>{c.AccountNumber}</td>
                        <td className={`${ui.td} font-mono text-xs`}>{c.ZoomCallId}</td>
                        <td className={ui.td}>
                          {/* Identical for Admins and Supervisors: GET admin/agents (roster
                              read) and POST admin/calls/{id}/assign are both on the
                              CommunicationSupervisor policy. */}
                          <div className="flex items-center gap-2">
                            <select
                              className={ui.control}
                              value={assignSelections[c.CallID] ?? ''}
                              onChange={e => setAssignSelections(prev => ({ ...prev, [c.CallID]: Number(e.target.value) }))}
                            >
                              <option value="">— select agent —</option>
                              {activeAgents.map(a => (
                                <option key={a.AgentID} value={a.AgentID}>
                                  {a.DisplayName ?? a.AzureADObjectID}{a.Email ? ` (${a.Email})` : ''}
                                </option>
                              ))}
                            </select>
                            <button className={ui.btnPrimary} disabled={!assignSelections[c.CallID]} onClick={() => handleAssignCall(c)}>
                              Assign
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {unmapped.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted">No unassigned calls.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pager page={unmappedPage} total={unmapped.length} pageSize={CALLS_PAGE_SIZE} onPage={setUnmappedPage} />
            </section>

            {me?.Environment && me.Environment !== 'Production' && (
              <section className={`${ui.sectionCard} border-dashed`}>
                <h2 className={ui.sectionTitle}>
                  Dev Simulator <span className="ml-1 text-xs font-normal text-muted">(non-production tool)</span>
                </h2>
                <p className="mb-4 mt-1 text-sm text-muted">
                  Simulate an inbound call for testing. This panel is only visible in non-production environments.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className={ui.label}>Caller #</label>
                    <input
                      type="text"
                      className={ui.control}
                      placeholder="e.g. +15551234567 (optional)"
                      value={simCallerNumber}
                      onChange={e => setSimCallerNumber(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={ui.label}>Route to agent</label>
                    <select className={ui.control} value={simAgentId} onChange={e => setSimAgentId(e.target.value === '' ? '' : Number(e.target.value))}>
                      <option value="">— unassigned —</option>
                      {activeAgents.filter(a => a.ZoomUserId).map(a => (
                        <option key={a.AgentID} value={a.AgentID}>
                          {a.DisplayName ?? a.AzureADObjectID}{a.Email ? ` (${a.Email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-secondary">
                    <input type="checkbox" className={ui.checkbox} checked={simMissed} onChange={e => setSimMissed(e.target.checked)} />
                    <span>Missed (nobody answered)</span>
                  </label>
                  <button className={ui.btnPrimary} onClick={handleSimulate} disabled={simBusy}>
                    {simBusy ? 'Simulating…' : simMissed ? 'Simulate missed call' : 'Simulate inbound call'}
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {/* ── Categories ───────────────────────────────────────── */}
        {tab === 'categories' && (
          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-4`}>Categories</h2>
            <ul className="divide-y divide-border-default">
              {categories.map(c => (
                <li key={c.CategoryID} className="flex items-center justify-between py-2 text-sm text-secondary">
                  <span>
                    <span className="font-medium text-primary">{c.Code}</span> — {c.Name}
                    {!c.IsActive && <span className="ml-2 text-muted">(inactive)</span>}
                  </span>
                  {c.IsActive && (
                    <button className={ui.btnDanger} onClick={async () => { await deactivateCategory(c.CategoryID); setCategories(await getCategories()); }}>
                      Deactivate
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Metrics (missed calls + graphs) ──────────────────── */}
        {tab === 'metrics' && (
          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-1`}>Missed Calls</h2>
            <p className="mb-4 text-sm text-muted">Customer-care queue calls. All times US Eastern (ET); weeks run Sunday–Saturday.</p>

            <div className="mb-6 flex flex-wrap items-end gap-3">
              <div>
                <label className={ui.label}>Date range</label>
                <select
                  className={ui.control}
                  value={metricsPreset}
                  onChange={e => setMetricsPreset(e.target.value as MetricsPreset)}
                >
                  {METRICS_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              {metricsPreset === 'custom' && (
                <>
                  <div>
                    <label className={ui.label}>Start date</label>
                    <input
                      type="date"
                      className={ui.control}
                      value={customFrom}
                      max={customTo || undefined}
                      onChange={e => setCustomFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={ui.label}>End date</label>
                    <input
                      type="date"
                      className={ui.control}
                      value={customTo}
                      min={customFrom || undefined}
                      onChange={e => setCustomTo(e.target.value)}
                    />
                  </div>
                </>
              )}
              <span className="pb-2 text-sm text-muted">
                {metricsRange
                  ? `${fmtRangeDate(metricsRange.from)} – ${fmtRangeDate(metricsRange.to)}`
                  : 'Pick a start and end date.'}
              </span>
            </div>
            {metrics && (
              <>
                <div className="mb-6 flex flex-wrap gap-4">
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Total Calls</div>
                    <div className="text-2xl font-bold text-primary">{metrics.AnsweredCount + metrics.MissedCount}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Answered</div>
                    <div className="text-2xl font-bold text-primary">{metrics.AnsweredCount}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Missed</div>
                    <div className="text-2xl font-bold text-red-600">{metrics.MissedCount}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted" title="Scoped to each day's configured business hours — after-hours calls never enter this ratio.">
                      Abandon rate
                    </div>
                    {abandonRateResult.kind === 'value' && (
                      <div className="text-2xl font-bold text-primary">{abandonRateResult.pct}%</div>
                    )}
                    {abandonRateResult.kind === 'no-data' && (
                      <div className="text-2xl font-bold text-primary">—</div>
                    )}
                    {abandonRateResult.kind === 'not-configured' && (
                      <div>
                        <div className="text-sm font-semibold text-muted">Not configured</div>
                        <button
                          type="button"
                          className="mt-0.5 text-xs font-medium text-blue-600 underline hover:text-blue-700"
                          onClick={() => setTab('settings')}
                        >
                          Set business hours →
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Avg Length of Call</div>
                    <div className="text-2xl font-bold text-primary">{fmtDur(metrics.AvgDurationSeconds)}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted" title="An overflow call always diverts to voicemail — this is a breakout of Missed, not a separate bucket.">
                      Overflow (Business Hours)
                    </div>
                    {metrics.BusinessHoursConfigured ? (
                      <div className="text-2xl font-bold text-violet-600">{metrics.OverflowBusinessHoursCount ?? '—'}</div>
                    ) : (
                      <div>
                        <div className="text-sm font-semibold text-muted">Not configured</div>
                        <button
                          type="button"
                          className="mt-0.5 text-xs font-medium text-blue-600 underline hover:text-blue-700"
                          onClick={() => setTab('settings')}
                        >
                          Set business hours →
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted" title="An overflow call always diverts to voicemail — this is a breakout of Missed, not a separate bucket.">
                      Overflow (After Hours)
                    </div>
                    {metrics.BusinessHoursConfigured ? (
                      <div className="text-2xl font-bold text-violet-600">{metrics.OverflowAfterHoursCount ?? '—'}</div>
                    ) : (
                      <div>
                        <div className="text-sm font-semibold text-muted">Not configured</div>
                        <button
                          type="button"
                          className="mt-0.5 text-xs font-medium text-blue-600 underline hover:text-blue-700"
                          onClick={() => setTab('settings')}
                        >
                          Set business hours →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {overflowCoverageIncomplete && (
                  <p className="-mt-4 mb-6 text-xs text-amber-700 dark:text-amber-400">
                    Only {overflowCoveragePct}% of calls in this range have been reconciled against Zoom's call history —
                    the two Overflow cards above may currently under-report until the Zoom API Sweep finishes
                    processing the rest of the range.
                  </p>
                )}

                <div className="mb-6">
                  <h3 className="mb-2 text-sm font-semibold text-secondary">Calls per day (answered vs. missed, ET)</h3>
                  <div style={{ height: 300 }}>
                    {dayData.length === 0 ? (
                      <div className="flex h-full items-center justify-center text-sm text-muted">No calls in range.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dayData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                          <XAxis dataKey="day" tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <Tooltip cursor={{ fill: 'rgba(148,163,184,0.15)' }} />
                          <Legend />
                          <Bar dataKey="answered" name="Answered" stackId="calls" fill="#16a34a" />
                          <Bar dataKey="missed" name="Missed" stackId="calls" fill="#dc2626" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Missed by hour of day (ET)</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hourData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                          <XAxis dataKey="hour" tick={axisTick} interval={1} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <Tooltip
                            cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                            labelFormatter={(h) => `${h}m ET`}
                            formatter={(v) => [String(v), 'Missed']}
                          />
                          <Bar dataKey="missed" fill="#dc2626" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Answered by agent</h3>
                    <div style={{ height: 260 }}>
                      {agentData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-muted">No answered calls.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={agentData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <YAxis type="category" dataKey="name" width={120} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <Tooltip
                              cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                              formatter={(v: any, _n: any, item: any) =>
                                [`${v} · avg call ${fmtDur(item?.payload?.avgDur)}`, 'Answered']}
                            />
                            <Bar dataKey="answered" fill="#2563eb" radius={[0, 3, 3, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    {/* Per-agent Answered keeps raw semantics on purpose (OQ-3 — Ames confirmed
                        not reclassifying it): an overflowed call still credits whichever agent
                        it routed to before diverting to voicemail. This chart's totals can
                        therefore run slightly ahead of the top "Answered" card above, which now
                        excludes every overflow call regardless of time of day. */}
                    <p className="mt-1 text-xs text-muted">
                      Unlike the Answered card above, this per-agent view isn't adjusted for Overflow —
                      an overflowed call still credits whichever agent it routed to.
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Calls by category (count)</h3>
                    {/* Height scales with the category count so every row fits; interval={0}
                        stops recharts from skipping Y-axis labels when space gets tight. */}
                    <div style={{ height: categoryData.length === 0 ? 260 : Math.max(260, categoryData.length * 32 + 40) }}>
                      {categoryData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-muted">No categorized calls in range.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={categoryData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <YAxis type="category" dataKey="name" width={180} interval={0} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <Tooltip cursor={{ fill: 'rgba(148,163,184,0.15)' }} formatter={(v) => [String(v), 'Calls']} />
                            <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                              {categoryData.map((c, i) => (
                                <Cell key={c.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Calls by category (%)</h3>
                    <div style={{ height: 260 }}>
                      {categoryData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-muted">No categorized calls in range.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                            <Pie
                              data={categoryData}
                              dataKey="count"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius="80%"
                              label={(entry: any) => `${entry.code} ${categoryPct(entry.count)}`}
                              labelLine={false}
                            >
                              {categoryData.map((c, i) => (
                                <Cell key={c.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any, n: any) => [`${v} calls (${categoryPct(Number(v))})`, n]} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted">
                      Percentages are of category assignments — a call with multiple categories counts once per category.
                    </p>
                  </div>
                </div>

                <h3 className="mb-2 mt-6 text-sm font-semibold text-secondary">Recent missed calls</h3>
                <div className={ui.tableWrap}>
                  <table className={ui.table}>
                    <thead className={ui.thead}>
                      <tr><th className={ui.th}>Time</th><th className={ui.th}>Caller</th><th className={ui.th}>Account</th><th className={ui.th}>Student</th><th className={ui.th}>Center</th></tr>
                    </thead>
                    <tbody className={ui.tbody}>
                      {metrics.RecentMissed.map(m => (
                        <tr key={m.CallID} className="hover:bg-surface-hover">
                          <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeET(m.CallTimeUtc)}</td>
                          <td className={ui.td}>{m.CallerNumber ? formatPhoneNumber(m.CallerNumber) : <span className="text-muted">—</span>}</td>
                          <td className={ui.td}>{m.AccountNumber || <span className="text-muted">—</span>}</td>
                          <td className={ui.td}>{m.StudentName || <span className="text-muted">—</span>}</td>
                          <td className={ui.td}>{m.SchoolName || <span className="text-muted">—</span>}</td>
                        </tr>
                      ))}
                      {metrics.RecentMissed.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted">No missed calls.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {/* ── Insights (call-volume timing, rep timeline, productivity, talk-time) ── */}
        {tab === 'insights' && <InsightsPanel categories={categories} onGoToAgentsTab={() => setTab('agents')} />}

        {/* ── Time Tracking (Workforce schema, read-only) ──────── */}
        {tab === 'timetracking' && <TimeTrackingAdmin />}

        {/* ── Settings ─────────────────────────────────────────── */}
        {tab === 'settings' && (
          <>
          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-1`}>Customer Care Call Queue</h2>
            <p className="mb-4 text-sm text-muted">
              The dialable extension of the Zoom call queue for Customer Care. It controls both which
              inbound calls get logged and which Zoom users appear in the agent-pairing dropdown.
              Changes take effect immediately.
            </p>

            {settings && (
              <p className="mb-4 text-xs text-muted">
                Current:{' '}
                <span className="font-medium text-secondary">
                  {settings.ZoomCallQueueExtension || '(none)'}
                </span>{' '}
                <span className="rounded bg-surface px-1.5 py-0.5">
                  source: {settings.ZoomCallQueueExtensionSource}
                </span>
                {settings.ZoomCallQueueExtensionSource === 'Database' && settings.UpdatedBy && (
                  <> · updated by {settings.UpdatedBy}</>
                )}
              </p>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className={ui.label}>Queue extension</label>
                <input
                  type="text"
                  className={ui.control}
                  placeholder="e.g. 9003 (test) / 1003 (prod)"
                  value={extensionInput}
                  onChange={e => { setExtensionInput(e.target.value); setSettingsMsg(null); setPreview(null); setPreviewError(null); }}
                />
              </div>
              <button
                className={ui.btnSecondary}
                onClick={handleVerifyExtension}
                disabled={previewing || !extensionInput.trim()}
              >
                {previewing ? 'Verifying…' : 'Verify against Zoom'}
              </button>
              <button className={ui.btnPrimary} onClick={handleSaveSettings} disabled={settingsSaving || !extensionDirty}>
                {settingsSaving ? 'Saving…' : 'Save'}
              </button>
              {settingsMsg && <span className="text-sm text-muted">{settingsMsg}</span>}
            </div>

            {preview && (
              <div className="mt-4 rounded-md border border-border-default bg-surface px-4 py-3 text-sm">
                {preview.Found ? (
                  <span className="text-secondary">
                    ✓ Matched queue <span className="font-medium text-primary">{preview.QueueName || preview.QueueId}</span>{' '}
                    with <span className="font-medium text-primary">{preview.MemberCount}</span> member{preview.MemberCount === 1 ? '' : 's'}.
                  </span>
                ) : (
                  <span className="text-red-600">No Zoom call queue has extension {preview.Extension}.</span>
                )}
              </div>
            )}
            {previewError && (
              <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
                {previewError}
              </div>
            )}
          </section>

          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-1`}>Business Hours</h2>
            <p className="mb-4 text-sm text-muted">
              Sets each day's Eastern Time window, used to split the Metrics tab's two Overflow
              cards and to scope the Abandon Rate to staffed hours. Every day starts{' '}
              <span className="font-medium text-secondary">Closed</span> — there's no default
              window, and a day stays after-hours-only until you turn it on and give it times.
              Overflow/Abandon Rate show "Not configured" on the Metrics tab until at least one
              day has hours set.
            </p>

            {settings && (
              <p className="mb-4 text-xs text-muted">
                Current:{' '}
                <span className="font-medium text-secondary">{fmtBusinessHoursSummary(settings.BusinessHours)}</span>
              </p>
            )}

            <div className="divide-y divide-border-default">
              {WEEKDAY_DISPLAY_ORDER.map(weekday => {
                const day = businessHoursInput.find(d => d.Weekday === weekday) as BusinessHoursDayInput;
                const label = WEEKDAY_LABELS[weekday];
                const invalid = day.open && !businessHoursDayValid(day);
                return (
                  <div key={weekday} className="flex flex-wrap items-center gap-3 py-2 first:pt-0 last:pb-0">
                    <div className="w-24 shrink-0 text-sm font-medium text-secondary">{label}</div>
                    <label className="flex items-center gap-2 text-sm text-secondary" htmlFor={`bh-open-toggle-${weekday}`}>
                      <input
                        id={`bh-open-toggle-${weekday}`}
                        type="checkbox"
                        className={ui.checkbox}
                        checked={day.open}
                        onChange={e => toggleBusinessHoursDayOpen(weekday, e.target.checked)}
                      />
                      <span>Open on {label}</span>
                    </label>
                    <div>
                      <label className="sr-only" htmlFor={`bh-open-${weekday}`}>{label} open time</label>
                      <input
                        id={`bh-open-${weekday}`}
                        type="time"
                        className={ui.control}
                        value={day.openTime}
                        disabled={!day.open}
                        onChange={e => updateBusinessHoursDay(weekday, { openTime: e.target.value })}
                      />
                    </div>
                    <span className="text-sm text-muted">to</span>
                    <div>
                      <label className="sr-only" htmlFor={`bh-close-${weekday}`}>{label} close time</label>
                      <input
                        id={`bh-close-${weekday}`}
                        type="time"
                        className={ui.control}
                        value={day.closeTime}
                        disabled={!day.open}
                        onChange={e => updateBusinessHoursDay(weekday, { closeTime: e.target.value })}
                      />
                    </div>
                    <button
                      type="button"
                      className={ui.btnSecondary}
                      disabled={!day.open}
                      onClick={() => copyBusinessHoursDayToAll(day)}
                    >
                      {`Copy ${label}'s hours to all days`}
                    </button>
                    {!day.open && <span className="text-xs text-muted">Closed — treated as after-hours all day.</span>}
                    {invalid && (
                      <p role="alert" className="w-full text-xs text-red-600">
                        {day.openTime.trim() === '' || day.closeTime.trim() === ''
                          ? `Enter both an open and close time for ${label}, or turn this day off.`
                          : `Close time must be after open time for ${label}.`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className={ui.btnPrimary}
                onClick={handleSaveBusinessHours}
                disabled={businessHoursSaving || !businessHoursDirty || !businessHoursValid}
              >
                {businessHoursSaving ? 'Saving…' : 'Save'}
              </button>
              {businessHoursMsg && <span className="text-sm text-muted">{businessHoursMsg}</span>}
            </div>
          </section>

          <ZoomSweepPanel />

          <TranscriptRetrievalPanel categories={categories} />
          </>
        )}
      </div>
    </PageLayout>
  );
};
